import React from "react";
import { BellIcon, ClipboardListIcon, FilePlus2Icon, HistoryIcon, InfoIcon, PrinterIcon, QrCodeIcon, ReceiptIcon, ScanLineIcon, SearchIcon, TruckIcon, WarehouseIcon, BoxIcon } from "lucide-react";
import { FieldRole, DemoUser } from "../../types/config";
import { FieldScreen } from "../../types/navigation";
import { useStore } from "../../contexts/StoreContext";
import { MobileHeader } from "../../components/mobile/MobileHeader";
import { CaseMiniCard } from "../../components/mobile/CaseMiniCard";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { bn, fmtDate, isDemoToday, nowIso } from "../../utils/bn";
interface Props {
  role: FieldRole;
  user: DemoUser;
  go: (s: FieldScreen) => void;
}
interface Action {
  label: string;
  icon: BoxIcon;
  onClick: () => void;
  primary?: boolean;
}
export function FieldHome({
  role,
  user,
  go
}: Props) {
  const {
    cases
  } = useStore();
  const myStation = 'তেজগাঁও ডাম্পিং স্টেশন';
  const today = cases.filter(c => isDemoToday(c.createdAt));
  const registered = cases.filter(c => c.stage === 'registered');
  const inTransit = cases.filter(c => c.stage === 'in_transit');
  const atMyStation = cases.filter(c => c.stage === 'at_station' && c.station?.station === myStation);
  const recent = [...cases].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  let kpis: {
    label: string;
    value: number;
    tone?: string;
  }[] = [];
  let actions: Action[] = [];
  let listTitle = '';
  let list = recent.slice(0, 4);
  if (role === 'sergeant') {
    kpis = [{
      label: 'আজকের হেফাজতে নেওয়া যানবাহন',
      value: today.length
    }, {
      label: 'চলমান রেকার অনুরোধ',
      value: registered.length,
      tone: 'text-warning-700'
    }, {
      label: 'অপেক্ষমাণ যানবাহন',
      value: registered.length + inTransit.length
    }, {
      label: 'আমার কার্যক্রম',
      value: cases.filter(c => c.sergeant === user.name).length
    }];
    actions = [{
      label: 'নতুন হেফাজত নির্দেশনা',
      icon: FilePlus2Icon,
      onClick: () => go({
        name: 'wizard'
      }),
      primary: true
    }, {
      label: 'রেকার অনুরোধ',
      icon: TruckIcon,
      onClick: () => go({
        name: 'pickup'
      })
    }, {
      label: 'যানবাহন অনুসন্ধান',
      icon: SearchIcon,
      onClick: () => go({
        name: 'records'
      })
    }, {
      label: 'হেফাজত ইতিহাস',
      icon: HistoryIcon,
      onClick: () => go({
        name: 'records'
      })
    }];
    listTitle = 'সাম্প্রতিক PCR';
  } else if (role === 'wrecker') {
    const pendingPrint = cases.filter(c => c.stage === 'registered' && !c.barcode.attached);
    kpis = [{
      label: 'আজকের নতুন PCR',
      value: today.length
    }, {
      label: 'চলমান পিকআপ',
      value: registered.length + inTransit.length,
      tone: 'text-warning-700'
    }, {
      label: 'স্টেশনে জমা দেওয়া',
      value: cases.filter(c => c.wreckerId === 'WRK-07' && c.station && isDemoToday(c.station.receivedAt)).length + 6
    }, {
      label: 'অপেক্ষমাণ প্রিন্ট',
      value: pendingPrint.length,
      tone: pendingPrint.length ? 'text-danger' : undefined
    }];
    const latest = recent.find(c => c.wreckerId === 'WRK-07');
    actions = [{
      label: 'নতুন PCR তৈরি করুন',
      icon: FilePlus2Icon,
      onClick: () => go({
        name: 'wizard'
      }),
      primary: true
    }, {
      label: 'যানবাহনের তথ্য',
      icon: InfoIcon,
      onClick: () => go({
        name: 'records'
      })
    }, {
      label: 'রসিদ প্রিন্ট',
      icon: ReceiptIcon,
      onClick: () => latest && go({
        name: 'receipt',
        pcr: latest.pcr
      })
    }, {
      label: 'বারকোড প্রিন্ট',
      icon: PrinterIcon,
      onClick: () => go({
        name: 'sticker',
        pcr: (pendingPrint[0] ?? latest)?.pcr ?? ''
      })
    }, {
      label: 'পিকআপ সম্পন্ন করুন',
      icon: TruckIcon,
      onClick: () => go({
        name: 'pickup'
      })
    }, {
      label: 'পূর্বের রেকর্ড',
      icon: HistoryIcon,
      onClick: () => go({
        name: 'records'
      })
    }];
    listTitle = 'নির্ধারিত পিকআপ';
    list = [...registered, ...inTransit];
  } else if (role === 'receiving') {
    kpis = [{
      label: 'আগত (পরিবহনাধীন)',
      value: inTransit.length,
      tone: 'text-warning-700'
    }, {
      label: 'আজ গৃহীত',
      value: atMyStation.filter(c => isDemoToday(c.station?.receivedAt)).length
    }, {
      label: 'স্টেশনে বর্তমান',
      value: atMyStation.length + 141
    }, {
      label: 'ধারণক্ষমতা',
      value: 220
    }];
    actions = [{
      label: 'বারকোড স্ক্যান করে গ্রহণ',
      icon: ScanLineIcon,
      onClick: () => go({
        name: 'receive'
      }),
      primary: true
    }, {
      label: 'স্টেশন ইনভেন্টরি',
      icon: WarehouseIcon,
      onClick: () => go({
        name: 'records'
      })
    }];
    listTitle = 'আগত যানবাহন';
    list = inTransit;
  } else {
    const approved = cases.filter(c => c.letter?.status === 'issued' && c.stage === 'at_station');
    kpis = [{
      label: 'অবমুক্তির অনুমোদনপ্রাপ্ত',
      value: approved.length,
      tone: 'text-success'
    }, {
      label: 'আজ হস্তান্তর',
      value: cases.filter(c => c.stage === 'released' && isDemoToday(c.letter?.redeemedAt)).length
    }, {
      label: 'স্টেশনে বর্তমান',
      value: atMyStation.length + 141
    }, {
      label: 'আটকানো স্ক্যান',
      value: cases.reduce((n, c) => n + c.history.filter(h => h.key === 'blocked_scan').length, 0),
      tone: 'text-danger'
    }];
    actions = [{
      label: 'অবমুক্তি পত্র যাচাই (QR)',
      icon: QrCodeIcon,
      onClick: () => go({
        name: 'release'
      }),
      primary: true
    }, {
      label: 'স্টেশন ইনভেন্টরি',
      icon: WarehouseIcon,
      onClick: () => go({
        name: 'records'
      })
    }];
    listTitle = 'অবমুক্তির জন্য প্রস্তুত';
    list = approved;
  }
  return <>
      <MobileHeader appName="ডিএমপি যানবাহন হেফাজত" right={<button className="relative p-1.5" aria-label="বিজ্ঞপ্তি" onClick={() => go({
      name: 'records'
    })} data-id="element-2014" data-mp="dckenkskmdug"><BellIcon className="h-5 w-5" data-id="element-2015" data-mp="29jmnnhmm1dm9" /><span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-warning" data-id="element-2016" data-mp="25g84ii3dkh4l" /></button>} data-id="element-2013" data-mp="ddvpg4kfolv2" />
      <div className="flex-1 overflow-y-auto no-scrollbar" data-id="element-2017" data-mp="2jfhciuhfg4xh">
        <div className="bg-navy px-4 pb-5 text-white" data-id="element-2018" data-mp="2jey3k39r8qvl">
          <p className="text-xs text-white/70" data-id="element-2019" data-mp="2egd2k1moi363">{fmtDate(nowIso())}</p>
          <p className="text-lg font-bold" data-id="element-2020" data-mp="1qr1yoba17snq">{user.name}</p>
          <p className="text-xs text-white/80" data-id="element-2021" data-mp="1qrlglrzomh1r">{user.title} · {user.unit}</p>
        </div>
        <div className="-mt-3 px-4" data-id="element-2022" data-mp="1lta6dg5h64j0">
          <div className="grid grid-cols-2 gap-2.5" data-id="element-2023" data-mp="1lq4gvzsxp0mr">
            {kpis.map(k => <div key={k.label} className="card p-3" data-id="element-2024" data-mp="20ig5730ehsqd">
                <p className={`text-2xl font-bold ${k.tone ?? 'text-navy'}`} data-id="element-2025" data-mp="1vqesxh38g20c">{bn(k.value)}</p>
                <p className="text-xs leading-snug text-ink-muted" data-id="element-2026" data-mp="180aurp8tcugq">{k.label}</p>
              </div>)}
          </div>

          <div className={`mt-4 grid gap-2.5 ${actions.length > 2 ? 'grid-cols-3' : 'grid-cols-2'}`} data-id="element-2027" data-mp="1329ax23kcr47">
            {actions.map(a => <button key={a.label} onClick={a.onClick} className={`flex flex-col items-center justify-center gap-1.5 rounded-xl p-3 text-center text-xs font-semibold leading-tight transition-colors duration-150 ${a.primary ? 'col-span-full flex-row bg-bdgreen py-3.5 text-sm text-white hover:bg-bdgreen-700' : 'border border-line bg-white text-ink hover:bg-navy-50'}`} data-id="element-2028" data-mp="3eo1ehhx4qrpl">
                <a.icon className={`h-5 w-5 ${a.primary ? '' : 'text-navy'}`} data-id="element-2029" data-mp="19ew67wvjdo7" />
                {a.label}
              </button>)}
          </div>

          <div className="mt-5 mb-4" data-id="element-2030" data-mp="68899z8912nu">
            <div className="mb-2 flex items-center justify-between" data-id="element-2031" data-mp="3oewd7lrkbjrx">
              <h2 className="text-sm font-bold text-ink" data-id="element-2032" data-mp="3ogqnm657jthr">{listTitle}</h2>
              <button className="text-xs font-semibold text-navy" onClick={() => go({
              name: 'records'
            })} data-id="element-2033" data-mp="3tceb8h41kel5">সব দেখুন</button>
            </div>
            {list.length === 0 ? <div className="card flex flex-col items-center p-6 text-center text-ink-muted" data-id="element-2034" data-mp="2hzlm7e2my48s">
                <ClipboardListIcon className="h-6 w-6" data-id="element-2035" data-mp="38poxpb0x29c5" />
                <p className="mt-1 text-sm" data-id="element-2036" data-mp="38maxa8btca7c">এই মুহূর্তে কোনো যানবাহন নেই।</p>
              </div> : <div className="space-y-2" data-id="element-2037" data-mp="34p50y8k997qj">
                {list.map(c => <CaseMiniCard key={c.pcr} c={c} onClick={() => {
              if (role === 'wrecker' && c.stage === 'registered') go({
                name: c.barcode.attached ? 'pickup' : 'sticker',
                pcr: c.pcr
              } as FieldScreen);else if (role === 'receiving' && c.stage === 'in_transit') go({
                name: 'receive'
              });else if (role === 'release' && c.letter?.status === 'issued') go({
                name: 'release'
              });else go({
                name: 'case',
                pcr: c.pcr
              });
            }} extra={role === 'wrecker' ? <span className="text-[11px] text-ink-soft" data-id="element-2039" data-mp="eyrxfgisph6s">{c.road}</span> : undefined} data-id="element-2038" data-mp="2zp0jogbiw8z9" />)}
              </div>}
          </div>
          {role === 'wrecker' && <p className="mb-4 flex items-center gap-1.5 text-[11px] text-ink-soft" data-id="element-2040" data-mp="a56xhixn6d4y"><StatusBadge label="GPS" tone="green" size="sm" data-id="element-2041" data-mp="hjpo09j13v1h" /> রেকার WRK-07 অবস্থান শেয়ার চালু (সিমুলেটেড)</p>}
        </div>
      </div>
    </>;
}